// Turbo 8 only prefetches links on mouseenter, and the native iOS/Android
// WebViews synthesize mouse events at the end of a tap, so prefetch gets no
// head start there. Dispatching a synthetic mouseenter on touchstart lets
// Turbo's own LinkPrefetchObserver run, eligibility and caching rules intact,
// while the finger is still down.
//
// Turbo waits 100ms of its own before performing the request, so the fetch
// begins 100ms into the touch and the gain is the rest of the tap dwell. That
// delay doubles as the scroll guard that made upstream drop touchstart
// prefetching: a touchmove past the threshold below dispatches mouseleave,
// clearing Turbo's pending timeout before any request goes out. A tap that
// ends first opts out the same way rather than leaving a wasted GET behind.

const MOVE_THRESHOLD = 10;

let startX = 0;
let startY = 0;
let prefetchedLink = null;
let performed = false;

const prefetchDisabled = () => {
  const meta = document.querySelector('meta[name="turbo-prefetch"]');
  return meta && meta.content === "false";
};

const isPrefetchable = (link) => {
  const href = link.getAttribute("href");
  if (!href || href.startsWith("#")) return false;
  if (/^(javascript|mailto|tel|sms):/i.test(href)) return false;
  if (link.hasAttribute("target") || link.hasAttribute("download")) return false;
  if (link.dataset.turboPrefetch === "false") return false;
  if (link.dataset.turbo === "false") return false;
  if (link.dataset.turboMethod) return false;
  return link.origin === window.location.origin;
};

const cancelPrefetch = () => {
  if (prefetchedLink) {
    prefetchedLink.dispatchEvent(new MouseEvent("mouseleave", { bubbles: false, cancelable: false }));
    prefetchedLink = null;
  }
};

document.addEventListener("touchstart", (event) => {
  prefetchedLink = null;
  performed = false;

  if (event.touches.length !== 1) return;
  if (prefetchDisabled()) return;

  const link = event.target.closest && event.target.closest("a[href]");
  if (!link || !isPrefetchable(link)) return;

  const touch = event.touches[0];
  startX = touch.clientX;
  startY = touch.clientY;

  prefetchedLink = link;
  link.dispatchEvent(new MouseEvent("mouseenter", { bubbles: false, cancelable: false }));
}, { capture: true, passive: true });

// Turbo dispatches this on the link when it actually performs the prefetch.
document.addEventListener("turbo:before-fetch-request", (event) => {
  if (event.target !== prefetchedLink) return;
  if (event.detail.fetchOptions?.headers?.["X-Sec-Purpose"] !== "prefetch") return;

  performed = true;
});

document.addEventListener("touchmove", (event) => {
  if (!prefetchedLink) return;

  const touch = event.touches[0];
  if (!touch) return;

  if (Math.hypot(touch.clientX - startX, touch.clientY - startY) > MOVE_THRESHOLD) cancelPrefetch();
}, { capture: true, passive: true });

// If the request is already in flight, leave it so the click can consume it.
document.addEventListener("touchend", () => {
  if (!performed) cancelPrefetch();
}, { capture: true, passive: true });

document.addEventListener("touchcancel", () => {
  cancelPrefetch();
}, { capture: true, passive: true });
